// @ts-nocheck
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as DragIndicatorIcon } from '../../assets/Icons/lucideIcons/grip-vertical.svg';
import { ReactComponent as CheckCircleOutlineIcon } from '../../assets/Icons/lucideIcons/circle-check.svg';
import { ReactComponent as DescriptionOutlinedIcon } from '../../assets/Icons/lucideIcons/file-text.svg';
import { ReactComponent as DownloadOutlinedIcon } from '../../assets/Icons/lucideIcons/arrow-down-to-line.svg';
import { ReactComponent as ErrorOutlineOutlinedIcon } from '../../assets/Icons/lucideIcons/circle-alert.svg';
import Draggable from 'react-draggable';
import { SnackbarContent, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { AI_NOTIFICATION_TOAST_STATUS } from '../../const/CommonConst';
import { parseSafeString } from '../../utils/Utils';

const clearActiveWeaknessAssessmentToast = (docId) => {
  if (!docId) {
    return;
  }

  const activeToasts = window?._activeWeaknessAssessmentToasts || {};

  delete activeToasts[docId];
  window._activeWeaknessAssessmentToasts = activeToasts;
};

const getToastPresentation = (status, palette, errorMessage) => {
  if (status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED) {
    return {
      headerBg: palette?.AINotification?.completedHeaderBg,
      headerTitle: 'Weakness assessment ready',
      iconBg: palette?.AINotification?.completedIconBg,
      iconColor: palette?.AINotification?.completedIconColor,
      subtitle: 'Document is ready. You can download it now.',
    };
  }

  if (status === AI_NOTIFICATION_TOAST_STATUS.FAILED) {
    return {
      headerBg: palette?.AINotification?.failedHeaderBg,
      headerTitle: 'Weakness assessment failed',
      iconBg: palette?.AINotification?.failedIconBg,
      iconColor: palette?.AINotification?.failedIconColor,
      subtitle: errorMessage,
    };
  }

  return {
    headerBg: palette?.AINotification?.inProgressHeaderBg,
    headerTitle: 'Weakness assessment in progress',
    iconBg: palette?.AINotification?.inProgressIconBg,
    iconColor: palette?.AINotification?.inProgressIconColor,
    subtitle: 'Preparing your weakness assessment document...',
  };
};

const downloadDocumentBlob = (fileData, docId) => {
  const blob = new Blob([fileData], { type: 'application/pdf' });
  const objectUrl = window.URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');

  anchorElement.href = objectUrl;
  anchorElement.download = `WeaknessAssessmentReport_${docId}.pdf`;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
  window.URL.revokeObjectURL(objectUrl);
};

const updateForwardedRef = (forwardedRef, element) => {
  if (typeof forwardedRef === 'function') {
    forwardedRef(element);
    return;
  }

  if (forwardedRef) {
    const mutableRef = forwardedRef;

    mutableRef.current = element;
  }
};

function InnerCard({ data, downloadWeaknessAssessmentDoc, onDismiss }) {
  const theme = useTheme();
  const requestStartedRef = useRef(false);
  const [status, setStatus] = useState(
    data?.status || AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [fileData, setFileData] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const documentLabel =
    data?.documentLabel || `Weakness Assessment ${data?.docId}`;

  const toastPresentation = useMemo(() => {
    return getToastPresentation(status, theme?.palette, errorMessage);
  }, [errorMessage, status, theme?.palette]);

  const prepareDocument = useCallback(() => {
    if (!data?.docId || !downloadWeaknessAssessmentDoc) {
      setStatus(AI_NOTIFICATION_TOAST_STATUS.FAILED);
      setErrorMessage('Weakness assessment document details are missing.');
      return;
    }

    setPdfPreviewUrl('');
    setStatus(AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS);
    setErrorMessage('');
    downloadWeaknessAssessmentDoc({ documentID: data?.docId })
      .then((response) => {
        setFileData(response?.data);

        const blob = new Blob([response?.data], { type: 'application/pdf' });
        const objectUrl = window.URL.createObjectURL(blob);
        setPdfPreviewUrl(objectUrl);

        setStatus(AI_NOTIFICATION_TOAST_STATUS.COMPLETED);
      })
      .catch((error) => {
        setStatus(AI_NOTIFICATION_TOAST_STATUS.FAILED);
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            'Unable to prepare the weakness assessment document.'
        );
      });
  }, [data?.docId, downloadWeaknessAssessmentDoc]);

  const handleDownload = useCallback(() => {
    if (!fileData || !data?.docId) {
      return;
    }
    downloadDocumentBlob(fileData, data?.docId);
  }, [data?.docId, fileData]);

  const handleOpenViewer = useCallback(() => {
    if (!pdfPreviewUrl) {
      return;
    }

    setIsViewerOpen(true);
  }, [pdfPreviewUrl]);

  const handleCloseViewer = useCallback(() => {
    setIsViewerOpen(false);
  }, []);

  const handleRetry = useCallback(() => {
    prepareDocument();
  }, [prepareDocument]);

  const renderStatusIcon = useCallback(() => {
    if (status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED) {
      return (
        <CheckCircleOutlineIcon
          fontSize="small"
          sx={{ color: toastPresentation?.iconColor }}
        />
      );
    }
    if (status === AI_NOTIFICATION_TOAST_STATUS.FAILED) {
      return (
        <ErrorOutlineOutlinedIcon
          width={18}
          height={18}
          sx={{ color: toastPresentation?.iconColor }}
        />
      );
    }

    return (
      <DescriptionOutlinedIcon
        fontSize="small"
        sx={{ color: toastPresentation?.iconColor }}
      />
    );
  }, [status, toastPresentation?.iconColor]);

  useEffect(() => {
    // Prevent duplicate requests in case of multiple re-renders
    if (requestStartedRef?.current) {
      return;
    }
    requestStartedRef.current = true;
    // Automatically start preparing the document when the toast appears
    prepareDocument();
  }, [prepareDocument]);

  useEffect(() => {
    // clean up the object URL when the component unmounts, coz after unmounting the toast,
    // browser still holds the pdf data in memory because of the object URL, which causes memory leak
    return () => {
      if (!pdfPreviewUrl) {
        return;
      }

      window.URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  return (
    <>
      <Card
        className="weakness-assessment-notification"
        sx={{
          width: 320,
          boxShadow: 6,
          borderRadius: 1,
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
        aria-live="polite"
        aria-label={toastPresentation?.headerTitle}
      >
        <Box
          className="weakness-assessment-notification-title"
          sx={{
            cursor: 'move',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: toastPresentation?.headerBg,
            color: theme?.palette?.AINotification?.headerTextColor,
            borderRadius: '4px 4px 0 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DragIndicatorIcon width={18} height={18} />
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              {toastPresentation?.headerTitle}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onDismiss}
            aria-label="Dismiss notification"
            sx={{
              color: theme?.palette?.AINotification?.headerTextColor,
            }}
          >
            <CloseIcon width={18} height={18} />
          </IconButton>
        </Box>

        {status === AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS && (
          <LinearProgress
            sx={{
              height: 3,
              backgroundColor:
                theme?.palette?.AINotification?.progressTrackColor,
              '& .MuiLinearProgress-bar': {
                backgroundColor:
                  theme?.palette?.AINotification?.progressBarColor,
              },
            }}
          />
        )}
        <Box
          sx={{
            padding: '12px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme?.palette?.AINotification?.bodyBg,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: toastPresentation?.iconBg,
              }}
            >
              {renderStatusIcon()}
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography variant="body2" noWrap title={documentLabel}>
                {documentLabel}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme?.palette?.AINotification?.subtitleColor,
                  display: 'block',
                  mt: 0.25,
                }}
              >
                {toastPresentation?.subtitle}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              backgroundColor: theme?.palette?.AINotification?.bodyBg,
            }}
          >
            {status === AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CircularProgress size={16} color="inherit" />}
                sx={{ flex: 1, textTransform: 'none' }}
                aria-label="Preparing weakness assessment document"
                disabled
              >
                Preparing
              </Button>
            )}

            {status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleDownload}
                  sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                  aria-label={`Download weakness assessment for ${documentLabel}`}
                  disabled={!fileData}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenViewer}
                  sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                  aria-label={`View weakness assessment for ${documentLabel}`}
                  disabled={!pdfPreviewUrl || isViewerOpen}
                >
                  View report
                </Button>
              </>
            )}

            {status === AI_NOTIFICATION_TOAST_STATUS.FAILED && (
              <Button
                variant="contained"
                size="small"
                onClick={handleRetry}
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                aria-label={`Retry weakness assessment for ${documentLabel}`}
              >
                Retry
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ErrorOutlineOutlinedIcon
              width={18}
              height={18}
              sx={{ color: theme?.palette?.AINotification?.subtitleColor }}
            />
            <Typography
              variant="caption"
              sx={{
                color: theme?.palette?.AINotification?.subtitleColor,
                fontStyle: 'italic',
              }}
            >
              AI results may be inaccurate, verify before use
            </Typography>
          </Box>
        </Box>
      </Card>
      <Dialog
        fullScreen
        open={isViewerOpen}
        onClose={handleCloseViewer}
        aria-labelledby={`weakness-assessment-preview-title-${data?.docId || 'default'}`}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme?.palette?.background?.default,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: `1px solid ${theme?.palette?.divider}`,
            }}
          >
            <Typography
              id={`weakness-assessment-preview-title-${data?.docId || 'default'}`}
              variant="h5"
            >
              {documentLabel}
            </Typography>
            <IconButton
              onClick={handleCloseViewer}
              aria-label="Close report preview"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, p: 2 }}>
            {pdfPreviewUrl && (
              <iframe
                title={`${documentLabel} preview`}
                src={pdfPreviewUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

InnerCard.propTypes = {
  data: PropTypes.shape({
    docId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentLabel: PropTypes.string,
    status: PropTypes.oneOf([
      AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS,
      AI_NOTIFICATION_TOAST_STATUS.COMPLETED,
      AI_NOTIFICATION_TOAST_STATUS.FAILED,
    ]),
  }).isRequired,
  downloadWeaknessAssessmentDoc: PropTypes.func,
  onDismiss: PropTypes.func.isRequired,
};

InnerCard.displayName = 'InnerCard';

const WeaknessAssessmentToast = forwardRef(
  function WeaknessAssessmentToast(props, ref) {
    const { id, message, downloadWeaknessAssessmentDoc } = props;
    const { closeSnackbar } = useSnackbar();
    const nodeRef = useRef(null);

    const data = parseSafeString(message, {
      docId: '',
      documentLabel: 'Weakness assessment document',
      status: AI_NOTIFICATION_TOAST_STATUS.FAILED,
    });

    useEffect(() => {
      return () => {
        // clear the active toast for this document when the component unmounts
        clearActiveWeaknessAssessmentToast(data?.docId);
      };
    }, [data?.docId]);

    const handleDismiss = useCallback(() => {
      // Clear the active toast for this document when the user dismisses it
      clearActiveWeaknessAssessmentToast(data?.docId);
      closeSnackbar(id);
    }, [closeSnackbar, data?.docId, id]);

    const setRefs = useCallback(
      (element) => {
        nodeRef.current = element;
        updateForwardedRef(ref, element);
      },
      [ref]
    );

    return (
      <Draggable
        handle=".weakness-assessment-notification-title"
        nodeRef={nodeRef}
      >
        <SnackbarContent ref={setRefs}>
          <InnerCard
            data={data}
            downloadWeaknessAssessmentDoc={downloadWeaknessAssessmentDoc}
            onDismiss={handleDismiss}
          />
        </SnackbarContent>
      </Draggable>
    );
  }
);

WeaknessAssessmentToast.propTypes = {
  downloadWeaknessAssessmentDoc: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.string.isRequired,
};
export default WeaknessAssessmentToast;
